/**
 * Generic autoplay for <slider-component> sliders.
 * Activates when the host element has `data-autoplay="true"`.
 * Reads interval (in seconds) from `data-autoplay-interval` (default 1s).
 * Pauses on hover, focus, user scroll/touch, when reduced motion is preferred,
 * or when the slider is offscreen.
 */
(function () {
  const PAUSE_AFTER_INTERACTION_MS = 2500;

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  class SliderAutoplay {
    constructor(host) {
      this.host = host;
      this.slider = host.querySelector('[id^="Slider-"]') || host.querySelector('.slider');
      if (!this.slider) return;

      this.intervalSeconds = parseFloat(host.dataset.autoplayInterval) || 1;
      this.timer = null;
      this.userPauseTimer = null;
      this.isPausedByUser = false;
      this.isVisible = true;

      this.bindEvents();
      this.observeVisibility();

      if (!prefersReducedMotion()) {
        this.start();
      }
    }

    bindEvents() {
      this.host.addEventListener('mouseenter', () => this.stop());
      this.host.addEventListener('mouseleave', () => this.maybeStart());
      this.host.addEventListener('focusin', () => this.stop());
      this.host.addEventListener('focusout', () => this.maybeStart());

      const onUserInteract = () => {
        this.isPausedByUser = true;
        this.stop();
        clearTimeout(this.userPauseTimer);
        this.userPauseTimer = setTimeout(() => {
          this.isPausedByUser = false;
          this.maybeStart();
        }, PAUSE_AFTER_INTERACTION_MS);
      };

      this.slider.addEventListener('touchstart', onUserInteract, { passive: true });
      this.slider.addEventListener('wheel', onUserInteract, { passive: true });
      this.slider.addEventListener('pointerdown', onUserInteract);
    }

    observeVisibility() {
      if (!('IntersectionObserver' in window)) return;
      const io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          this.isVisible = entry.isIntersecting;
          if (this.isVisible) this.maybeStart();
          else this.stop();
        });
      });
      io.observe(this.host);
    }

    start() {
      if (this.timer) return;
      this.timer = setInterval(() => this.advance(), this.intervalSeconds * 1000);
    }

    stop() {
      if (!this.timer) return;
      clearInterval(this.timer);
      this.timer = null;
    }

    maybeStart() {
      if (this.isPausedByUser || !this.isVisible || prefersReducedMotion()) return;
      this.start();
    }

    advance() {
      const slide = this.slider;
      const items = slide.querySelectorAll('[id^="Slide-"]');
      if (!items.length) return;
      const itemOffset = items[1] ? items[1].offsetLeft - items[0].offsetLeft : items[0].clientWidth;
      const maxScroll = slide.scrollWidth - slide.clientWidth;
      const atEnd = slide.scrollLeft >= maxScroll - 1;
      let target;
      if (atEnd) {
        target = 0;
      } else {
        target = Math.min(slide.scrollLeft + itemOffset, maxScroll);
      }
      slide.scrollTo({ left: target, behavior: 'smooth' });
    }
  }

  function init(root = document) {
    root.querySelectorAll('slider-component[data-autoplay="true"]').forEach((host) => {
      if (host.__autoplayInit) return;
      host.__autoplayInit = true;
      new SliderAutoplay(host);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
